/**
 * Водитель CSGOPositive: страницы матча нет, событие ищем на главной.
 *
 * Человек: два имени → строка в «Текущие» или лупа «Предстоящие» → клик
 * по своей стороне. Повторяем. Ставку не жмём.
 *
 * Гостю `a.m_open` ведёт в `#register` — строку находим, клик не делаем.
 * Два матча с теми же именами — отказ: дешевле, чем чужая строка.
 */

import { tap, typeQuery as typeInto } from './predictfun';
import { coordinates } from './coordinates';
import { showCell } from './showCell';
import { decimalEqual, decimalText, currencyOf } from './receipt';
import type { CouponReceipt } from './coupon';
import type { DriveAsk } from './run';
import { positiveMarketMatches } from './positiveMarkets';

const DEAD = /\b(finished_event|won_1|won_2)\b/;

export function confirmOff(venue: string): boolean {
  return venue === 'csgopositive';
}

export function searchQuery(who: readonly string[]): string {
  return who.filter((name) => name.trim() !== '').join(' ');
}

export function searchField(root: ParentNode): HTMLInputElement | null {
  const node = root.querySelector('#upcoming_search');
  return node instanceof HTMLInputElement && !node.disabled ? node : null;
}

export function searchOpener(root: ParentNode): HTMLElement | null {
  const node = root.querySelector('a.toggle_search, .toggle_search');
  return node instanceof HTMLElement ? node : null;
}

export function searchShown(root: ParentNode): boolean {
  const box = root.querySelector('.search_match');
  if (!(box instanceof HTMLElement)) return false;
  return !box.classList.contains('hidden');
}

export function guest(root: ParentNode): boolean {
  for (const node of root.querySelectorAll('a.register, a.login, a.register.m_open, a.login.m_open')) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.classList.contains('left') || node.classList.contains('right')) continue;
    if (node.closest('.event')) continue;
    return true;
  }
  return false;
}

export function eventById(root: ParentNode, eid: string): HTMLElement | null {
  const id = eid.trim();
  if (!/^\d+$/.test(id)) return null;
  const nodes = root.querySelectorAll(`.event[data-id="${id}"]`);
  const node = nodes.length === 1 ? nodes[0] : null;
  if (!(node instanceof HTMLElement) || !liveEvent(node)) return null;
  return node;
}

export function selEid(sel: string | undefined): string {
  if (typeof sel !== 'string') return '';
  const hit = sel.trim().match(/^(\d+)~positive~$/);
  return hit?.[1] ?? '';
}

/** Страницы матча нет — id кладём в hash сами, площадка его не читает. */
export function eidFromUrl(url: string | undefined): string {
  if (typeof url !== 'string' || url === '') return '';
  try {
    const parsed = new URL(url, 'https://csgopositive.me/');
    const fromQuery = parsed.searchParams.get('e');
    if (fromQuery && /^\d+$/.test(fromQuery)) return fromQuery;
    const fromHash = /(?:^|[&#])e=(\d+)\b/.exec(parsed.hash);
    return fromHash?.[1] ?? '';
  } catch {
    return '';
  }
}

const FIT_CSS = '#welcome_slider,.welcome_mascot_viewport,.background_profile_big,.bg_parallax{display:none!important}'
  + '#header{position:relative!important;top:auto!important}'
  + '#content,#current_matches_block{margin-top:0!important}'
  + '.event{scroll-margin-top:12px}';

/** Баннер площадки на ноутбуке съедает первый экран — матчи уезжают под сгиб. */
export function fitViewport(doc: Document): void {
  const view = doc.defaultView;
  const tall = view !== null && view.innerHeight >= 900 && view.innerWidth >= 1200;
  if (tall) {
    doc.documentElement.classList.remove('forq-cspos-fit');
    return;
  }
  doc.documentElement.classList.add('forq-cspos-fit');
  if (doc.getElementById('forq-cspos-fit')) return;
  const style = doc.createElement('style');
  style.id = 'forq-cspos-fit';
  style.textContent = FIT_CSS;
  doc.documentElement.appendChild(style);
}

const AIM = 'forq-cspos-aim';
const AIM_CSS = `.${AIM}{outline:2px solid #3dff7a;outline-offset:2px;border-radius:8px}`;

export function showEvent(event: HTMLElement): void {
  const root = event.ownerDocument;
  if (root) {
    for (const old of root.querySelectorAll(`.${AIM}`)) {
      if (old !== event) old.classList.remove(AIM);
    }
    if (!root.getElementById('forq-cspos-aim-css')) {
      const style = root.createElement('style');
      style.id = 'forq-cspos-aim-css';
      style.textContent = AIM_CSS;
      root.documentElement.appendChild(style);
    }
    event.classList.add(AIM);
  }
  showCell(event);
}

function sideName(el: Element | null): string {
  if (el === null) return '';
  const named = el.querySelector('.team_name')?.textContent ?? '';
  if (named.trim() !== '') return named;
  return el.querySelector('img')?.getAttribute('alt') ?? '';
}

function nameHit(want: string, got: string): boolean {
  // Shared prediction abbreviations allow prefixes and initials. Here those
  // can confuse T1/T1 Academy or Zero/ex-Zero. Only cosmetic suffixes differ.
  // "KaBuM! Ilha das Lendas" ↔ page "KaBum! IDL": same first token, rest = initials.
  const key = (name: string) => name.normalize('NFKC').toLowerCase().trim()
    .replace(/\s+/g, ' ').replace(/\s+(?:gaming|e-?sports)$/, '');
  const a = key(want);
  const b = key(got);
  if (a !== '' && a === b) return true;
  const words = (s: string) => s.replace(/[!.,]/g, ' ').split(/\s+/).filter(Boolean);
  const left = words(a);
  const right = words(b);
  if (left[0] === undefined || left[0] !== right[0] || left.length === right.length) return false;
  const [short, long] = left.length < right.length ? [left, right] : [right, left];
  const rest = short.slice(1).join('');
  const initials = long.slice(1).map((w) => w[0] ?? '').join('');
  return rest !== '' && rest === initials;
}

export function eventHits(who: readonly string[], event: Element): boolean {
  const names = who.filter((name) => name.trim() !== '');
  if (names.length !== 2 || nameHit(names[0]!, names[1]!)) return false;
  const left = sideName(event.querySelector('a.left'));
  const right = sideName(event.querySelector('a.right'));
  if (left === '' || right === '') return false;
  const [one, two] = names;
  if (one === undefined || two === undefined) return false;
  return (
    (nameHit(one, left) && nameHit(two, right)) || (nameHit(one, right) && nameHit(two, left))
  );
}

export function liveEvent(el: Element): boolean {
  if (!el.classList.contains('event') || !el.hasAttribute('data-id')) return false;
  return !DEAD.test(typeof el.className === 'string' ? el.className : '');
}

export function eventsOf(root: ParentNode): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const node of root.querySelectorAll('.event[data-id]')) {
    if (!(node instanceof HTMLElement) || !liveEvent(node)) continue;
    out.push(node);
  }
  return out;
}

export function ownEvents(root: ParentNode, who: readonly string[]): HTMLElement[] {
  return eventsOf(root).filter((event) => eventHits(who, event));
}

/** Ровно один свой матч. Ноль или два — не гадаем. */
export function firstOwnEvent(root: ParentNode, who: readonly string[]): HTMLElement | null {
  const hits = ownEvents(root, who);
  return hits.length === 1 ? hits[0]! : null;
}

export function pickSide(event: Element, pick: string): HTMLElement | null {
  if (pick.trim() === '') return null;
  const left = event.querySelector('a.left.m_open');
  const right = event.querySelector('a.right.m_open');
  const hits = [left, right].filter((node): node is HTMLElement => {
    return node instanceof HTMLElement && nameHit(pick, sideName(node));
  });
  return hits.length === 1 ? hits[0]! : null;
}

/** The home row opens the catalogue; only its match WIN price is evidence.
 * Other supported markets must be verified by the native coupon code.
 * Map WIN (`win_N_N` in the coupon) is the same code live and prematch. */
export function supportedAsk(ask: DriveAsk): boolean {
  const c = coordinates(ask);
  if (!c || c.subject) return false;
  if (c.kind === 'WIN' && c.period === 'M') return true;
  const map = /^P[1-9]\d*$/.test(c.period);
  if (c.kind === 'WIN' && map) return true;
  if (ask.eventLine !== 'prematch') return false;
  return ['HCP', 'TTL'].includes(c.kind)
    && (c.scope === 'PE' && c.period === 'M' || c.scope === 'PO' && map && ask.sport === 'cs2');
}

export function rowMatches(event: Element, ask: DriveAsk): boolean {
  const sport: Record<string, string> = { '730': 'cs2', '570': 'dota2', '101': 'lol', '103': 'valorant', '106': 'mlbb' };
  return sport[event.getAttribute('data-app_id') ?? ''] === ask.sport
    && eventHits(ask.who ?? [], event);
}

/** Only the match winner price is shown on the entry row. */
export function rowPriceApplies(ask: DriveAsk): boolean {
  const c = coordinates(ask); return c?.kind === 'WIN' && c.period === 'M';
}

/** Totals have no team; either entry opens the same event market catalogue. */
export function totalEntry(event: HTMLElement, ask: DriveAsk): HTMLElement | null {
  return coordinates(ask)?.kind === 'TTL' ? event.querySelector<HTMLElement>('a.left.m_open') : null;
}

export function eventMatches(event: Element, ask: DriveAsk): boolean {
  return supportedAsk(ask) && rowMatches(event, ask);
}

export function availableSide(cell: HTMLElement): boolean {
  const price = cell.querySelector('.sum.odds_icon');
  return cell.isConnected && price !== null && !cell.matches('.disabled, .locked, [aria-disabled="true"]')
    && !price.matches('.deactivated, .disabled, .locked');
}

export function priceMatches(cell: HTMLElement, odds: string): boolean {
  return availableSide(cell) && decimalEqual(cell.querySelector('.sum.odds_icon')?.textContent ?? '', odds);
}

/** The native site sets these fields only when the market modal has loaded.
 * Reading them avoids reopening the same event and clearing a user's form. */
export function alreadyOpened(doc: Document, event: HTMLElement, cell: HTMLElement): boolean {
  const eid = doc.querySelector<HTMLInputElement>('input#eid')?.value;
  const tid = doc.querySelector<HTMLInputElement>('input#tid')?.value;
  const visible = ['#bet', '#bet_next'].some(selector => {
    const modal = doc.querySelector<HTMLElement>(selector);
    return !!modal && !modal.hidden && modal.style.display !== 'none'
      && doc.defaultView?.getComputedStyle(modal).display !== 'none';
  });
  return visible && eid === event.dataset.id && tid === (cell.classList.contains('left') ? '1' : '2');
}

function inputValue(doc: Document, id: string): string | null {
  const fields = doc.querySelectorAll<HTMLInputElement>(`input#${id}`);
  return fields.length === 1 ? fields[0]!.value : null;
}

/** Native script_market.js copies m_next.data-type into #bt before showing
 * #bet_next. Calls without an ask retain the legacy match-WIN-only contract. */
export function winnerMarket(doc: Document, event: HTMLElement, cell: HTMLElement, ask?: DriveAsk): HTMLElement | null {
  const team = cell.classList.contains('left') ? '1' : '2';
  if (inputValue(doc, 'eid') !== event.dataset.id || inputValue(doc, 'tid') !== team) return null;
  const hits = [...doc.querySelectorAll<HTMLElement>('#bet .m_next[data-type]')].filter(el =>
    (ask ? positiveMarketMatches(el.dataset.type ?? '', ask, team === '1' ? 'H' : 'A')
      : ['win_' + team, 'live:win_' + team].includes(el.dataset.type ?? ''))
    && el.getAttribute('href') === '#bet_next'
    && !el.matches('.disabled, .locked, [aria-disabled="true"]'));
  return hits.length === 1 ? hits[0]! : null;
}

export function winnerReceipt(doc: Document, event: HTMLElement, cell: HTMLElement, ask?: DriveAsk): CouponReceipt | null {
  const team = cell.classList.contains('left') ? '1' : '2';
  const scope = doc.querySelector<HTMLElement>('#bet_next');
  if (!scope || scope.hidden || scope.style.display === 'none' || doc.defaultView?.getComputedStyle(scope).display === 'none'
    || !event.isConnected || !liveEvent(event)
    || ((ask === undefined || rowPriceApplies(ask)) && !availableSide(cell))
    || inputValue(doc, 'eid') !== event.dataset.id || inputValue(doc, 'tid') !== team
    || !(ask ? positiveMarketMatches(inputValue(doc, 'bt') ?? '', ask, team === '1' ? 'H' : 'A')
      : ['win_' + team, 'live:win_' + team].includes(inputValue(doc, 'bt') ?? ''))
    || scope.querySelector('#place_bet_btn.locked, .bet_koef.locked, .bet_koef.deactivated')) return null;
  const prices = scope.querySelectorAll<HTMLElement>('.bet_koef');
  if (prices.length !== 1) return null;
  const price = prices[0]!;
  const odds = decimalText(price.textContent ?? '');
  if (odds === null || !decimalEqual(odds, inputValue(doc, 'koef_val') ?? '')) return null;
  const currency = scope.querySelector<HTMLElement>('#bet_currency .selected');
  let code = currency ? currencyOf(currency) : null;
  const text = currency?.textContent?.trim() ?? '';
  if (!code && /^[A-Z]{3,6}$/.test(text)) code = text;
  if (!code && currency) {
    const icons = [...currency.querySelectorAll('i')];
    const codes = new Set(icons.flatMap(icon => {
      const found = ['btc', 'ltc', 'eth', 'trx', 'usdt', 'bnb'].filter(c => icon.classList.contains(c));
      return icon.classList.contains('rr') ? ['RUB'] : found.map(c => c.toUpperCase());
    }));
    if (codes.size === 1) code = [...codes][0]!;
  }
  if (inputValue(doc, 'gem_bet') === '1' || scope.querySelector('#bet_currency.freebet_selected')) code = null;
  return { scope, price, odds, currency: code };
}

export { tap, typeInto as typeQuery };
